import {
  getLeoUuid,
  LeoUUID,
  getList,
  getObject,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TableFilter, TableFilterDTO, TableFilterEnums } from "../types/tableFilter";
import { SurveyViewResults, SurveyViewResultsDTO, SurveyViewResultsEnums } from "./surveyViewResults";
/* eslint-disable import/export */
export abstract class GetPaginatedSurveyViewResultsRPC implements
  LeoRPC<GetPaginatedSurveyViewResultsRPC.Request, GetPaginatedSurveyViewResultsRPC.Response, GetPaginatedSurveyViewResultsRPC.Errors.Errors>
{
  abstract execute(
    request: GetPaginatedSurveyViewResultsRPC.Request
  ): Promise<LeoRPCResult<GetPaginatedSurveyViewResultsRPC.Response, GetPaginatedSurveyViewResultsRPC.Errors.Errors>>;
}

export namespace GetPaginatedSurveyViewResultsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
    currentQuestionViewIds: string[];
    filters: TableFilterDTO[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPaginatedSurveyViewResultsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
    readonly currentQuestionViewIds: LeoUUID[];
    readonly filters: TableFilter[];
  
    static readonly constraints = {
      "filters": {
        "maxSize": 5,
      }
    }
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID, 
    currentQuestionViewIds: LeoUUID[], 
    filters: TableFilter[]
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
      this.currentQuestionViewIds = currentQuestionViewIds;
      Request.validateFilters(filters);
      this.filters = filters;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      const currentQuestionViewIds = getList<string, LeoUUID>(
        dto,
        "currentQuestionViewIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestCurrentQuestionViewIdsError
      );
      const filters = getList<TableFilterDTO, TableFilter>(
        dto,
        "filters",
        TableFilter.fromDTO,
        InvalidRequestFiltersError
      );
      return new Request(
        projectId,
        surveyId,
        surveyViewId,
        currentQuestionViewIds,
        filters
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO(),
          currentQuestionViewIds: this.currentQuestionViewIds.map((e: LeoUUID) => { return e.toDTO(); }),
          filters: this.filters.map((e: TableFilter) => { return e.toDTO() })
      };
      return dto;
    }
    private static validateFilters = (filters: TableFilter[]): void => {
      if(filters.length > 5) {
        throw new InvalidRequestFiltersMaxSizeError(
          5,
          `Attribute filters is too long. Size is ${filters.length}. Max size is 5.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPaginatedSurveyViewResultsRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}',currentQuestionViewIds='${this.currentQuestionViewIds}',filters='${this.filters}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyViewResults: SurveyViewResultsDTO;
    isSPoCChangeGraphAllowed: boolean;
    isMasterView: boolean;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPaginatedSurveyViewResultsResponse: boolean = true;
  
    readonly surveyViewResults: SurveyViewResults;
    readonly isSPoCChangeGraphAllowed: boolean;
    readonly isMasterView: boolean;
  
  
    constructor(
    surveyViewResults: SurveyViewResults, 
    isSPoCChangeGraphAllowed: boolean, 
    isMasterView: boolean
    ) {
      this.surveyViewResults = surveyViewResults;
      this.isSPoCChangeGraphAllowed = isSPoCChangeGraphAllowed;
      this.isMasterView = isMasterView;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyViewResults = SurveyViewResults.fromDTO(getObject(dto, "surveyViewResults", InvalidResponseSurveyViewResultsError));
      const isSPoCChangeGraphAllowed = getBoolean(dto, "isSPoCChangeGraphAllowed", InvalidResponseIsSPoCChangeGraphAllowedError);
      const isMasterView = getBoolean(dto, "isMasterView", InvalidResponseIsMasterViewError);
      return new Response(
        surveyViewResults,
        isSPoCChangeGraphAllowed,
        isMasterView
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyViewResults: this.surveyViewResults.toDTO(),
          isSPoCChangeGraphAllowed: this.isSPoCChangeGraphAllowed,
          isMasterView: this.isMasterView
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPaginatedSurveyViewResults(surveyViewResults='${this.surveyViewResults}',isSPoCChangeGraphAllowed='${this.isSPoCChangeGraphAllowed}',isMasterView='${this.isMasterView}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID",
    InvalidQuestionViewId = "INVALID_QUESTION_VIEW_ID",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPaginatedSurveyViewResultsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetPaginatedSurveyViewResultsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `GetPaginatedSurveyViewResultsRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    
    
    export interface InvalidQuestionViewIdDTO {
      code: string;
    }
    
    export class InvalidQuestionViewId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionViewId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionViewIdDTO {
        const dto: InvalidQuestionViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionViewId.prototype.toString = function toString(): string {
      return `GetPaginatedSurveyViewResultsRPC.INVALID_QUESTION_VIEW_ID()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `GetPaginatedSurveyViewResultsRPC.DELETED_COLUMN_FILTER()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidSurveyViewId | InvalidQuestionViewId | DeletedColumnFilter;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidSurveyViewIdDTO | InvalidQuestionViewIdDTO | DeletedColumnFilterDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        case RPCError.InvalidQuestionViewId:
          return new InvalidQuestionViewId();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPaginatedSurveyViewResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends GetPaginatedSurveyViewResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends GetPaginatedSurveyViewResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidRequestCurrentQuestionViewIdsError extends GetPaginatedSurveyViewResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCurrentQuestionViewIdsError";
  }
}

export class InvalidRequestFiltersError extends GetPaginatedSurveyViewResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersError";
  }
}

export class InvalidRequestFiltersMaxSizeError extends InvalidRequestFiltersError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersMaxSizeError";
  }
}

export class InvalidResponseSurveyViewResultsError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyViewResultsError";
  }
}

export class InvalidResponseIsSPoCChangeGraphAllowedError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsSPoCChangeGraphAllowedError";
  }
}

export class InvalidResponseIsMasterViewError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsMasterViewError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

export class InvalidResponseInvalidQuestionViewIdError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionViewIdError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends GetPaginatedSurveyViewResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

